/**
 * Hook quản lý khách hàng (managed users) — super admin chỉnh sửa thông tin riêng tư.
 *
 * Luồng chính:
 * - Tải danh sách khách hàng khi mount.
 * - Chọn user → đồng bộ privateForm từ dữ liệu user.
 * - Lưu: validate CCCD (12 chữ số nếu có), gọi API cập nhật.
 */
import { useEffect, useState } from 'react';
import { UI_MESSAGES } from '../../../../shared/constants/messages';
import { TEXT } from '../../../../shared/languages';
import { getApiErrorMessage } from '../../../../shared/utils/apiErrors';
import { managedUserApi } from '../api/managedUserApi';

/** Form thông tin riêng tư rỗng mặc định */
const emptyPrivateForm = {
  residential_address: '',
  hometown: '',
  residence: '',
  age: '',
  cccd: '',
};

/** Ánh xạ user từ API sang form chỉnh sửa */
const mapUserToPrivateForm = (user) => ({
  residential_address: user?.residential_address || '',
  hometown: user?.hometown || '',
  residence: user?.residence || '',
  age: user?.age ? String(user.age) : '',
  cccd: user?.cccd || '',
});

/**
 * Hook quản lý state trang AdminManagedUsersPage.
 * @returns {Object} Danh sách user, form riêng tư, trạng thái và handlers
 */
export const useAdminManagedUsers = () => {
  const [userList, setUserList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [privateForm, setPrivateForm] = useState(emptyPrivateForm);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cccdError, setCccdError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const managedUsers = TEXT.admin.managedUsers;

  const loadManagedUsers = async () => {
    setIsLoadingUsers(true);

    try {
      const response = await managedUserApi.getManagedUsers();
      setUserList(response.data.data);
    } catch (requestError) {
      setErrorMessage(getApiErrorMessage(requestError, UI_MESSAGES.managedUsersLoadError));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadManagedUsers();
  }, []);

  const selectedUser = userList.find((user) => String(user.id) === String(selectedUserId)) || null;

  // Đồng bộ form khi chọn user khác hoặc bỏ chọn
  useEffect(() => {
    if (!selectedUser) {
      setPrivateForm(emptyPrivateForm);
      return;
    }

    setPrivateForm(mapUserToPrivateForm(selectedUser));
    setCccdError('');
  }, [selectedUser]);

  const selectUser = (userId) => {
    setSelectedUserId(userId);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const updatePrivateField = (fieldName, fieldValue) => {
    if (fieldName === 'cccd') {
      setCccdError('');
    }

    setPrivateForm((currentForm) => ({
      ...currentForm,
      [fieldName]: fieldValue,
    }));
  };

  const savePrivateInfo = async (event) => {
    event.preventDefault();

    if (!selectedUser) {
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    setCccdError('');
    setSuccessMessage('');

    const normalizedCccd = privateForm.cccd.replace(/\D/g, '');

    // CCCD Việt Nam: 12 chữ số nếu được nhập
    if (normalizedCccd && normalizedCccd.length !== 12) {
      setCccdError(TEXT.validation.cccdInvalid);
      setIsSaving(false);
      return;
    }

    try {
      const response = await managedUserApi.updateManagedUser(selectedUser.id, {
        residential_address: privateForm.residential_address.trim() || null,
        hometown: privateForm.hometown.trim() || null,
        residence: privateForm.residence.trim() || null,
        age: privateForm.age ? Number(privateForm.age) : null,
        cccd: normalizedCccd || null,
      });

      const updatedUser = response.data.data;
      setUserList((currentList) =>
        currentList.map((user) => (user.id === updatedUser.id ? updatedUser : user))
      );
      setSuccessMessage(managedUsers.updateSuccess);
    } catch (requestError) {
      setErrorMessage(getApiErrorMessage(requestError, UI_MESSAGES.managedUserUpdateError));
    } finally {
      setIsSaving(false);
    }
  };

  return {
    userList,
    selectedUser,
    selectedUserId,
    privateForm,
    isLoadingUsers,
    isSaving,
    errorMessage,
    cccdError,
    successMessage,
    selectUser,
    updatePrivateField,
    savePrivateInfo,
  };
};
